import React, { useImperativeHandle, useState, useRef } from 'react';
import styled from 'astroturf/react';
import useResizeObserver, {
  UseResizeObserverCallback,
} from '@react-hook/resize-observer';

// Stub out ResizeObserver for server-side rendering
class ServerSideResizeObserverPolyfill {
  constructor() {}
  observe() {}
  unobserve() {}
}

function useResizeObserverSsr<T extends Element>(
  target: React.RefObject<T> | React.ForwardedRef<T> | T | null,
  callback: UseResizeObserverCallback,
) {
  return useResizeObserver(target, callback, {
    polyfill:
      typeof window === 'undefined' ? ServerSideResizeObserverPolyfill : null,
  });
}

import { Body, BaseBodyProps } from './Body';
import { BodyLink } from './link/BodyLink';

const Container = styled.div`
  position: relative;
`;

const TruncateBody = styled(Body)`
  overflow-wrap: anywhere;
`;

const ReadMoreContainer = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  background: #fff;

  &::before {
    content: '';
    position: absolute;
    height: 100%;
    width: 40px;
    left: -40px;
    bottom: 0;
    background: linear-gradient(to right, transparent, white 80%);
  }
`;

type TruncateProps = React.PropsWithChildren<{
  bodyProps?: Omit<BaseBodyProps, 'size'>;
  lines?: number;
  size?: BaseBodyProps['size'];
}> &
  React.HTMLAttributes<HTMLDivElement>;

/**
 * This is a wrapper around the Body component that truncates text after a certain number of
 * lines, and displays a "Read more" link to fully expand the text - but only if the text actually
 * overflows the container.
 *
 * The internal Body component can be configured by passing props via `bodyProps`. However, the
 * `size` prop must be passed directly to Truncate.
 *
 * Props:
 * - `size`: Controls the font-size and line-height of the heading (default: 'md')
 *           Choose between 'xs' | 'sm' | 'md' | 'lg' | 'xl'
 * - `lines`: How many lines of text to display before truncating. (default: 3)
 * - `bodyProps`: Extra props to pass to the internal Body component.
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Truncate } from 'js/design_system/Truncate';
 *
 * <Truncate>A simple paragraph in medium-sized font, truncated to 3 lines.</Truncate>
 * ```
 * ---
 *
 * ## Custom size and lines
 *
 * @example
 *
 * ```tsx
 * import { Truncate } from 'js/design_system/Truncate';
 *
 * <Truncate size='lg' lines={4}>A larger paragraph, truncated to 4 lines.</Truncate>
 * ```
 * ---
 *
 */
export const Truncate = React.forwardRef<HTMLDivElement, TruncateProps>(
  ({ bodyProps, lines = 3, size = 'md', children, ...restProps }, ref) => {
    const [expanded, setExpanded] = useState(lines ? false : true);
    const [needsTruncate, setNeedsTruncate] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);

    // Note: This resize observer is used to dynamically tell if the text overflows the container
    // and needs truncating. This can be a bit expensive performance-wise, which is why this isn't
    // just built into the Body component.
    useResizeObserverSsr(containerRef, (entry) => {
      if (!needsTruncate) {
        const container = entry.target as HTMLDivElement;
        setNeedsTruncate(container.scrollHeight > container.offsetHeight);
      }
    });

    if (lines <= 0) {
      throw new Error('Truncate: lines must be greater than 0');
    }

    if (!children) {
      return null;
    }

    return (
      <Container
        ref={containerRef}
        {...restProps}
        style={
          expanded
            ? restProps.style
            : {
                ...restProps.style,
                // This depends on the --type-body-sm-line-height,
                // --type-body-lg-line-height etc tokens being set.
                // If this component isn't working, check that a token
                // mode is set on the page, or that the tokens didn't change.
                maxHeight: `calc(var(--type-body-${size}-line-height) * ${lines})`,
                overflow: 'hidden',
              }
        }
      >
        <TruncateBody size={size} {...bodyProps}>
          {children}
        </TruncateBody>
        {needsTruncate && !expanded ? (
          <ReadMoreContainer>
            <Body size={size} as="span">
              ...&nbsp;
            </Body>
            <BodyLink
              size={size}
              underline
              onClick={() => {
                setExpanded(true);
              }}
            >
              Read more
            </BodyLink>
          </ReadMoreContainer>
        ) : null}
      </Container>
    );
  },
);
